import React, { useEffect, useState } from 'react';
import {
  Avatar,
  IconButton,
  Typography,
  Alert,
  Menu,
  MenuItem,
  Box,
  TextField,
  Select,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { red } from '@mui/material/colors';
import RenegocierePanel from './RenegocierePanel';
import QcFooterActions from './QcFooterActions';
import Modal from '@mui/material/Modal';
import CreareRaport from '../../CreareRaportQC';
import axios from 'axios';

const modalStyle = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  width: '80%',
  height: '80%',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  overflowY: 'auto',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
};

const QcCard = ({
  issueId,
  cardStyle,
  qcStatus,
  anchorEl,
  onMenuClick,
  onMenuClose,
  onReportClick,
  openReportModal,
  onCloseReportModal,
  currentPrice,
  newPrice,
  acceptedPrice,
  fallbackPrice,
  onChangeNewPrice,
  onSaveNewPrice,
  isAcceptingPrice,
  onRejectPrice,
  jiraFields,
  vendorData,
  onCreateActAditional,
  isCreatingAct,
  creationFeedback,
  additionalActPreview,
  onChangeStatus,
  expanded,
  onToggleExpand,
  rejectionReason,
  onChangeRejectionReason,
  awbData,
  onAwbInputChange,
  onGenerateAwbAndReport,
  showRenegotiation,
}) => {
  const [qcReportAttachment, setQcReportAttachment] = useState(null);
  const [loadingQcAttachment, setLoadingQcAttachment] = useState(false);
  const [openPdfError, setOpenPdfError] = useState(null);
  const renegotiationAutoReason =
    "Propunerea de preț înaintată în cadrul procesului de renegociere nu a fost acceptată, astfel produsul rămâne respins în urma verificării Quality Check.";
  const isFakeStatus = qcStatus === 'Fake';
  const isRestituitStatus = qcStatus === 'Restituit';
  const requiresRejectionReason = qcStatus === 'Respins';
  const autoRenegotiationReasonActive = showRenegotiation && requiresRejectionReason;
  const awbStrictStatus = isFakeStatus || isRestituitStatus;
  const requiredAwbFilled =
    Boolean(awbData?.street) &&
    Boolean(awbData?.number) &&
    Boolean(awbData?.postalCode) &&
    Boolean(awbData?.city) &&
    Boolean(awbData?.county);
  const effectiveRejectionReason = autoRenegotiationReasonActive
    ? renegotiationAutoReason
    : rejectionReason;
  const isRejectionReasonFilled = Boolean(effectiveRejectionReason?.trim());
  const shouldRenderAwbSection =
    qcStatus !== 'Acceptat' &&
    (showRenegotiation ||
      awbStrictStatus ||
      (requiresRejectionReason && isRejectionReasonFilled));

  const statusChipStyles = (() => {
    const defaultStyle = { bg: '#FBBF24', color: '#FFFFFF' };
    if (qcStatus === 'Acceptat') {
      return { bg: '#22C55E', color: '#FFFFFF' };
    }
    if (qcStatus === 'Respins') {
      return { bg: '#FACC15', color: '#FFFFFF' };
    }
    if (qcStatus === 'Fake') {
      return { bg: '#EF4444', color: '#FFFFFF' };
    }
    return defaultStyle;
  })();

  const fancyInputStyles = {
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 2,
    '& .MuiOutlinedInput-root': {
      color: '#F5F5F5',
      '& fieldset': {
        borderColor: 'rgba(255,255,255,0.25)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255,255,255,0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#FACC15',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255,255,255,0.7)',
      '&.Mui-focused': {
        color: '#FACC15',
      },
    },
    '& .MuiSelect-select': {
      color: '#F5F5F5',
    },
    '& .MuiOutlinedInput-input': {
      color: '#F5F5F5',
    },
  };

  useEffect(() => {
    const fetchQcAttachment = async () => {
      if (!issueId) {
        setQcReportAttachment(null);
        return;
      }
      setLoadingQcAttachment(true);
      setOpenPdfError(null);
      try {
        const { data } = await axios.get(
          `https://server-9p4hx.kinsta.app/api/tasks/jira-task/${issueId}`
        );
        const attachments = data?.attachments || [];
        const pdfAttachment =
          attachments.find(
            (att) =>
              att?.filename &&
              att.filename.startsWith('QualityCheck_') &&
              /\.pdf$/i.test(att.filename)
          ) ||
          attachments.find(
            (att) =>
              att?.filename &&
              att.filename.startsWith('QualityCheck_') &&
              (att?.mimeType ||
                att?.mimetype ||
                att?.contentType ||
                '')
                .toLowerCase()
                .includes('pdf')
          );
        setQcReportAttachment(pdfAttachment || null);
      } catch (error) {
        console.error('Error fetching QC attachments:', error);
        setQcReportAttachment(null);
      } finally {
        setLoadingQcAttachment(false);
      }
    };
    fetchQcAttachment();
  }, [issueId, qcStatus]);

  const handleOpenQcPdf = () => {
    setOpenPdfError(null);
    if (!qcReportAttachment?.id) {
      setOpenPdfError(
        'Nu există un raport QC disponibil pentru acest produs momentan.'
      );
      return;
    }
    const url = `https://server-9p4hx.kinsta.app/api/tasks/attachments/${qcReportAttachment.id}`;
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening QC PDF:', error);
      setOpenPdfError('Nu am putut deschide fișierul PDF.');
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        mb: 2,
        borderRadius: 5,
        p: 3,
        background: 'linear-gradient(145deg, #131207, #1f2209, #4b3f07)',
        color: '#F5F5F5',
        boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
        ...cardStyle,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Avatar
            sx={{
              bgcolor: red[500],
              width: 56,
              height: 56,
              fontWeight: 700,
              fontSize: 20,
              boxShadow: '0 10px 30px rgba(255,255,255,0.25)',
            }}
          >
            QC
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Quality Check
            </Typography>
            <Box
              sx={{
                mt: 0.5,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.5,
                borderRadius: 999,
                background: statusChipStyles.bg,
                color: statusChipStyles.color,
                fontWeight: 700,
                letterSpacing: 0.5,
                boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
              }}
            >
              Status: {qcStatus}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {qcReportAttachment && (
            <Tooltip title="Deschide PDF QC">
              <span>
                <IconButton
                  onClick={handleOpenQcPdf}
                  disabled={loadingQcAttachment}
                  sx={{
                    color: '#FDE68A',
                    border: '1px solid rgba(253,230,138,0.5)',
                    '&:hover': {
                      background: 'rgba(253,230,138,0.15)',
                    },
                  }}
                >
                  <PictureAsPdfIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <IconButton
            aria-label="settings"
            onClick={onMenuClick}
            sx={{
              color: '#FFE08A',
              border: '1px solid rgba(255,255,255,0.2)',
              '&:hover': {
                background: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>
      {openPdfError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setOpenPdfError(null)}
        >
          {openPdfError}
        </Alert>
      )}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onMenuClose}>
        <MenuItem onClick={onReportClick}>Creare Raport</MenuItem>
      </Menu>
      <Alert
        severity="info"
        sx={{
          backgroundColor: 'rgba(255,255,255,0.08)',
          color: '#FFE08A',
          borderRadius: 3,
          fontWeight: 500,
          mb: 3,
        }}
      >
        Te rog pregătește pozele produsului înainte de crearea raportului.
      </Alert>

      {showRenegotiation && (
        <Box
          sx={{
            borderRadius: 4,
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)',
            p: 2,
            mb: 3,
            boxShadow: 'inset 0 0 40px rgba(255,255,255,0.06)',
          }}
        >
          <RenegocierePanel
            currentPrice={currentPrice}
            newPrice={newPrice}
            acceptedPrice={acceptedPrice}
            fallbackPrice={fallbackPrice}
            onChangeNewPrice={onChangeNewPrice}
            onSaveNewPrice={onSaveNewPrice}
            isAcceptingPrice={isAcceptingPrice}
            onRejectPrice={onRejectPrice}
            jiraFields={jiraFields}
            vendorData={vendorData}
            onCreateActAditional={onCreateActAditional}
            isCreatingAct={isCreatingAct}
            creationFeedback={creationFeedback}
            additionalActPreview={additionalActPreview}
          />
        </Box>
      )}
      {requiresRejectionReason && (
        <Box
          sx={{
            mt: 3,
            borderRadius: 4,
            background: 'rgba(0,0,0,0.25)',
            border: '1px solid rgba(255,255,255,0.08)',
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ color: '#1E90FF', fontWeight: 'bold', marginBottom: 2 }}>
            Motiv Respins
          </Typography>
          {autoRenegotiationReasonActive ? (
            <Box
              sx={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 3,
                p: 2,
                color: '#FFFFFF',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {renegotiationAutoReason}
            </Box>
          ) : (
            <>
              <Select
                value={rejectionReason}
                onChange={(e) => onChangeRejectionReason(e.target.value)}
                displayEmpty
                fullWidth
                sx={{ marginBottom: 2, ...fancyInputStyles }}
                error={requiresRejectionReason && !isRejectionReasonFilled}
              >
                <MenuItem value="" disabled>Selecteaza motivul</MenuItem>
                <MenuItem value="Stare generala uzata: Produsul prezinta semne evidente de uzura, cum ar fi zgarieturi, pete sau decolorari, care nu respecta standardele noastre de calitate.">Stare generala uzata...</MenuItem>
                <MenuItem value="Defecte structurale: Produsul are defecte structurale, cum ar fi cusaturi desfacute, fermoare defecte sau parti lipsa, care afecteaza functionalitatea si estetica.">Defecte structurale...</MenuItem>
                <MenuItem value="Piese neoriginale sau inlocuite: Am constatat ca anumite componente ale produsului nu sunt originale sau au fost inlocuite, ceea ce afecteaza autenticitatea si valoarea acestuia.">Piese neoriginale...</MenuItem>
                <MenuItem value="Semne de reparatii neautorizate: Produsul prezinta semne de reparatii sau modificari care nu au fost realizate de un specialist autorizat, ceea ce reduce valoarea si integritatea sa.">Semne de reparatii neautorizate...</MenuItem>
                <MenuItem value="Lipsa documentatiei necesare: Produsul nu este insotit de documentatia necesara, cum ar fi certificate de autenticitate, facturi sau alte acte, ceea ce ne impiedica sa confirmam provenienta si autenticitatea.">Lipsa documentatiei...</MenuItem>
              </Select>
              {requiresRejectionReason && !isRejectionReasonFilled && (
                <Typography variant="caption" color="error">
                  Selectează un motiv pentru respingere înainte de generarea raportului.
                </Typography>
              )}
            </>
          )}
        </Box>
      )}

      {shouldRenderAwbSection && (
        <Box
          sx={{
            borderRadius: 4,
            background: 'rgba(0,0,0,0.25)',
            border: '1px solid rgba(255,255,255,0.08)',
            p: 3,
            mb: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: isFakeStatus
                ? '#FF7A18'
                : isRestituitStatus
                  ? '#4ADE80'
                  : requiresRejectionReason
                    ? '#FDBA74'
                    : '#FDE68A',
              fontWeight: 700,
              letterSpacing: 0.5,
              mb: 2,
            }}
          >
            {isFakeStatus
              ? 'Produs Contrafăcut - AWB'
              : isRestituitStatus
                ? 'Produs Restituit - AWB'
                : requiresRejectionReason
                  ? 'Produs Respins - AWB'
                  : 'Generare AWB'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <TextField
              label="Strada"
              variant="outlined"
              fullWidth
              name="street"
              value={awbData.street}
              onChange={onAwbInputChange}
              sx={fancyInputStyles}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Nr"
                variant="outlined"
                fullWidth
                name="number"
                value={awbData.number}
                onChange={onAwbInputChange}
                sx={fancyInputStyles}
              />
              <TextField
                label="Cod poștal"
                variant="outlined"
                fullWidth
                name="postalCode"
                value={awbData.postalCode}
                onChange={onAwbInputChange}
                sx={fancyInputStyles}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Oraș"
                variant="outlined"
                fullWidth
                name="city"
                value={awbData.city}
                onChange={onAwbInputChange}
                sx={fancyInputStyles}
              />
              <TextField
                label="Județ"
                variant="outlined"
                fullWidth
                name="county"
                value={awbData.county}
                onChange={onAwbInputChange}
                sx={fancyInputStyles}
              />
            </Box>
            <TextField
              label="Observații"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              name="observations"
              value={awbData.observations}
              onChange={onAwbInputChange}
              sx={fancyInputStyles}
            />
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <IconButton
              color="primary"
              disabled={
                (shouldRenderAwbSection && !requiredAwbFilled) ||
                (requiresRejectionReason && !isRejectionReasonFilled)
              }
              onClick={() => {
                if (
                  (!shouldRenderAwbSection || requiredAwbFilled) &&
                  (!requiresRejectionReason || isRejectionReasonFilled)
                ) {
                  onGenerateAwbAndReport?.();
                }
              }}
              sx={{
                px: 3,
                borderRadius: 3,
                background: 'linear-gradient(120deg, #FBBF24, #F97316)',
                color: '#1F1303',
                '&.Mui-disabled': {
                  background: 'rgba(255,255,255,0.2)',
                  color: 'rgba(255,255,255,0.6)',
                },
              }}
            >
              <Typography variant="button" sx={{ fontWeight: 700 }}>
                {isFakeStatus
                  ? 'Generează AWB & Fișa Fake'
                  : isRestituitStatus
                    ? 'Generează AWB & Fișa Restituit'
                    : requiresRejectionReason
                      ? 'Generează AWB & Fișa Respins'
                      : 'Generează AWB & Raport'}
              </Typography>
            </IconButton>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          borderTop: '1px solid rgba(255,255,255,0.1)',
          mt: 3,
          pt: 3,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <QcFooterActions
          onChangeStatus={onChangeStatus}
          expanded={expanded}
          onToggleExpand={onToggleExpand}
        />
      </Box>

      <Modal
        open={openReportModal}
        onClose={(e, reason) => {
          if (reason !== 'backdropClick') onCloseReportModal();
        }}
        disableEscapeKeyDown
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{ ...modalStyle, position: 'relative' }}>
          <IconButton
            aria-label="Close"
            onClick={onCloseReportModal}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <CreareRaport issueId={issueId} onBack={onCloseReportModal} />
        </Box>
      </Modal>
    </Box>
  );
};

export default QcCard;
